/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useState } from 'react'

import { Alert, AssetDetailModal, SimpleTable, TableSkeleton } from '@/components/ui'
import { useDashboard, useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { validateDateRangeWithin13Months } from '@/insights/common/formatUtils'
import genericLocationDrilldown from '@/insights/common/genericLocationDrilldown'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { DateOptions } from '@/insights/components/CustomSingleSelect'
import {
  ApiService,
  AssetTypeFilter,
  type InsightDefinition,
  type InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { getSpeedUnit, kphToUser } from '@/utils/conversions'
import { downloadCsv } from '@/utils/csvExport'
import { formatDate, standardDateTimeFormat } from '@/utils/formatDate'

import { SPEED_TYPE, SPEED_TYPE_OPTIONS } from './constants'

const PATH = '/insights/reports/speed-exceptions'

interface SpeedExceptionParams extends InsightParametersType {
  companyId: number
  assetIds: string
  speedType: string
  speedThreshold: number
  date?: string
  from?: string
  to?: string
}

interface SpeedExceptionRecord {
  assetId: number
  assetName: string
  sampleTime: Date | string
  speed: number
  location: string
  addressState: string
  latitude: number
  longitude: number
}

const SpeedException: InsightDefinition<SpeedExceptionParams, SpeedExceptionRecord[]> = {
  active: true,
  slug: 'report/speed-exceptions',
  category: 'report',
  name: 'Speed Exceptions by Asset',
  description: 'View speed exception events across assets.',
  defaultWidth: 'full',
  defaultAspectRatio: 16 / 9,

  configuration: {
    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      {
        id: 'assetIds',
        control: 'AssetDropdownMulti',
        required: true,
        includeSelectAll: true,
        assetType: AssetTypeFilter.InTransit,
      },
      {
        id: 'speedType',
        control: 'CustomSingleSelect',
        required: true,
        defaultValue: SPEED_TYPE.AVERAGE,
        label: 'Speed type',
        options: SPEED_TYPE_OPTIONS,
      },
      {
        id: 'speedThreshold',
        control: 'SpeedInput',
        defaultValue: 0,
        min: 0,
        label: 'Speed threshold',
        showDefault: true,
      },
      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: DateOptions,
      },
      {
        id: 'customDate',
        label: 'Date',
        control: 'DateTimeRange',
        dependsOn: 'date',
        dependsOnValue: 'custom',
      },
    ],

    isValid: (configuration: SpeedExceptionParams) => {
      if (configuration.date === 'custom' && !configuration.from && !configuration.to) {
        return 'Enter the custom date for Raised Time'
      }

      if (configuration.date === 'custom') {
        if (configuration.from && configuration.to) {
          const fromDate = new Date(configuration.from)
          const toDate = new Date(configuration.to)
          if (fromDate > toDate) {
            return 'Start date must be before end date'
          }
        }
        const dateValidation = validateDateRangeWithin13Months(configuration.from, configuration.to)
        if (dateValidation !== true) {
          return dateValidation
        }
      }

      if (configuration.companyId && configuration.assetIds?.length) {
        return true
      }

      return 'Company and at least one Asset are required.'
    },
  },

  load: async (api: ApiService, parameters: SpeedExceptionParams) => {
    const path = `${PATH}?${buildQuery({ ...parameters, speedType: parameters.speedType ?? SPEED_TYPE.AVERAGE })}`
    const response = await api.get<SpeedExceptionRecord[]>({ path, origin: 'SpeedExceptions' })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data?.length) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  csv: async (api: ApiService, parameters: SpeedExceptionParams, title: string) => {
    await downloadCsv(api, PATH, parameters, title)
  },

  Display: ({ status, data, configuration }) => {
    const [selectedAsset, setSelectedAsset] = useState<number | undefined>()
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    const parameters = configuration.parameters as SpeedExceptionParams

    const units = useDashboard().configuration.units as 'Imperial' | 'Metric'
    const speedUnit = getSpeedUnit(units)

    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (showSkeleton) {
      // This could be handled by the framework by just returning the
      // same status. But we should make the skeleton match the final
      // table result if possible.
      return <TableSkeleton columns={16} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      // Wait until we're ready to display before displaying
      // Framework will handle the other states like error and no data.
      // We could handle no data here with a custom message like
      // "No history found".
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }
    if (!data?.length) {
      // Probably would never get here, but keeps typescript happy.
      return null
    }

    const body = data.slice().map((row, _) => ({
      Asset: (
        <button onClick={() => setSelectedAsset(row.assetId)} className="text-left block text-blue-500 cursor-pointer">
          {row.assetName}
        </button>
      ),
      Date: formatDate(row.sampleTime, standardDateTimeFormat),
      Speed: kphToUser(row.speed, speedUnit),
      State: row.addressState,
      Location: genericLocationDrilldown({
        configuration,
        address: row.location ?? undefined,
        latitude: row.latitude ?? undefined,
        longitude: row.longitude ?? undefined,
        companyId: parameters.companyId,
        markerFields: {
          Asset: row.assetName,
          Time: formatDate(row.sampleTime, standardDateTimeFormat),
        },
      }),
    }))

    const dataItem = body[0]
    const headers = dataItem
      ? Object.keys(dataItem)
      : Array(10)
          .fill(null)
          .map((_, index) => `placeholder-${index}`)

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body} cellClassNames={{ Date: 'text-nowrap' }} />
        </div>
        <AssetDetailModal
          show={selectedAsset !== undefined}
          onHide={() => setSelectedAsset(undefined)}
          assetId={selectedAsset}
          companyId={parameters.companyId}
        />
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const { companies } = useCompany()

    if (status.code === InsightStatusCode.ERROR) {
      // Framework will handle this
      return ''
    }
    const fields: Record<string, string> = {}
    const params = configuration.parameters as SpeedExceptionParams
    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (params.assetIds?.length) {
      fields[''] = `${params.assetIds.length} Asset${params.assetIds.length === 1 ? '' : 's'}`
    }
    addDateToCaptionFields({ ...params, fields, finite: true })
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING || (status.code === InsightStatusCode.ERROR && data === undefined)) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default SpeedException
